<script setup>
import { computed } from 'vue';

import WootLabel from 'dashboard/components-next/label/Label.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

import { useAutomationOperationsFormatters } from './composables/useAutomationOperationsFormatters';

defineProps({
  destinations: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
});

const { t, deliveryStateCounts, destinationRowState, formatDate, formatStatus } =
  useAutomationOperationsFormatters();

const destinationHeaders = computed(() => [
  t('AUTOMATION.OPERATIONS.OUTBOX_DESTINATIONS.TABLE_HEADER.DESTINATION'),
  t('AUTOMATION.OPERATIONS.OUTBOX_DESTINATIONS.TABLE_HEADER.QUEUE'),
  t('AUTOMATION.OPERATIONS.OUTBOX_DESTINATIONS.TABLE_HEADER.FAILURES'),
  t('AUTOMATION.OPERATIONS.OUTBOX_DESTINATIONS.TABLE_HEADER.DLQ'),
  t('AUTOMATION.OPERATIONS.OUTBOX_DESTINATIONS.TABLE_HEADER.LAST_FAILURE'),
  t('AUTOMATION.OPERATIONS.OUTBOX_DESTINATIONS.TABLE_HEADER.PROVIDER'),
]);
</script>

<template>
  <div class="mb-3 flex items-center justify-between gap-3">
    <span class="text-heading-3 text-n-slate-12">
      {{ $t('AUTOMATION.OPERATIONS.OUTBOX_DESTINATIONS.TITLE') }}
    </span>
    <WootLabel
      :label="destinations.length.toString()"
      :color="destinations.length ? 'amber' : 'teal'"
      compact
    />
  </div>
  <BaseTable
    :headers="destinationHeaders"
    :items="destinations"
    :loading="isLoading"
    :loading-message="$t('AUTOMATION.OPERATIONS.HEALTH.LOADING')"
    :no-data-message="$t('AUTOMATION.OPERATIONS.OUTBOX_DESTINATIONS.EMPTY')"
  >
    <template #row="{ items }">
      <BaseTableRow
        v-for="destination in items"
        :key="destination.destination"
        :item="destination"
        :state="destinationRowState(destination)"
      >
        <template #default>
          <BaseTableCell class="min-w-56">
            <div class="flex min-w-0 flex-col gap-1">
              <span class="truncate text-body-main text-n-slate-12">
                {{ destination.destination }}
              </span>
              <span class="truncate text-body-main text-n-slate-11">
                {{ destination.event_type || '-' }}
              </span>
            </div>
          </BaseTableCell>
          <BaseTableCell class="min-w-52">
            <div class="flex flex-wrap gap-x-3 gap-y-1 text-body-main">
              <span class="tabular-nums text-n-slate-12">
                {{
                  $t('AUTOMATION.OPERATIONS.OUTBOX_DESTINATIONS.PENDING', {
                    count: destination.pending_count || 0,
                  })
                }}
              </span>
              <span class="tabular-nums text-n-slate-11">
                {{
                  $t('AUTOMATION.OPERATIONS.OUTBOX_DESTINATIONS.PROCESSING', {
                    count: destination.processing_count || 0,
                  })
                }}
              </span>
              <span
                v-if="destination.expired_processing_count"
                class="tabular-nums text-n-amber-11"
              >
                {{
                  $t('AUTOMATION.OPERATIONS.OUTBOX_DESTINATIONS.EXPIRED', {
                    count: destination.expired_processing_count,
                  })
                }}
              </span>
            </div>
          </BaseTableCell>
          <BaseTableCell class="min-w-48">
            <div class="flex min-w-0 flex-col gap-1">
              <div class="flex flex-wrap gap-x-3 gap-y-1">
                <span class="tabular-nums text-body-main text-n-slate-12">
                  {{
                    $t('AUTOMATION.OPERATIONS.OUTBOX_DESTINATIONS.FAILED', {
                      count: destination.failed_count || 0,
                    })
                  }}
                </span>
                <span class="tabular-nums text-body-main text-n-slate-11">
                  {{
                    $t(
                      'AUTOMATION.OPERATIONS.OUTBOX_DESTINATIONS.RECENT_FAILED',
                      {
                        count: destination.recent_failed_delivery_count || 0,
                      }
                    )
                  }}
                </span>
              </div>
              <span class="truncate text-body-main text-n-slate-11">
                {{ deliveryStateCounts(destination) || '-' }}
              </span>
            </div>
          </BaseTableCell>
          <BaseTableCell class="min-w-32">
            <span class="tabular-nums text-body-main text-n-slate-12">
              {{ destination.dead_letter_count || 0 }}
            </span>
          </BaseTableCell>
          <BaseTableCell class="min-w-56">
            <div class="flex min-w-0 flex-col gap-1">
              <span class="truncate text-body-main text-n-slate-12">
                {{ destination.latest_error_class || '-' }}
              </span>
              <span class="truncate text-body-main text-n-slate-11">
                {{ formatStatus(destination.latest_failure_classification) }}
              </span>
              <span class="text-body-main text-n-slate-11">
                {{ formatDate(destination.latest_failure_at) }}
              </span>
            </div>
          </BaseTableCell>
          <BaseTableCell class="min-w-44">
            <div class="flex min-w-0 flex-col gap-1">
              <span class="truncate text-body-main text-n-slate-12">
                {{ destination.latest_provider || '-' }}
              </span>
              <span class="text-body-main text-n-slate-11">
                {{ formatDate(destination.latest_delivery_at) }}
              </span>
            </div>
          </BaseTableCell>
        </template>
      </BaseTableRow>
    </template>
  </BaseTable>
</template>
